import { useCallback, useEffect, useRef } from 'react'
import {
  CLOCK_HEALTHY_RESET_MS,
  CLOCK_VERSION_CHECK_MS,
  bundleSignatureFromDocument,
  bundleSignatureFromHtml,
  parseRecoveryState,
  staleReloadDue,
  versionReloadDue,
  type ClockRecoveryState,
} from '../lib/clockRecovery.js'

const STATE_KEY = 'trafficplay.clock.recovery'
const TICK_MS = 5_000

interface Options {
  displayId: string
  token: string | null
  /** Faux tant que le premier bootstrap n'a pas abouti : rien à rétablir. */
  active: boolean
  stale: boolean
  wsConnected: boolean
}

function readState(): ClockRecoveryState {
  try { return parseRecoveryState(sessionStorage.getItem(STATE_KEY)) } catch { return parseRecoveryState(null) }
}

function writeState(state: ClockRecoveryState) {
  try { sessionStorage.setItem(STATE_KEY, JSON.stringify(state)) } catch { /* kiosque sans stockage */ }
}

/**
 * Page d'accueil servie par le serveur, lue sans cache. Sert à la fois de
 * sonde (« le serveur répond-il ? ») et de source de la version publiée.
 * `null` = serveur injoignable ou réponse d'erreur : on ne recharge pas, sinon
 * le navigateur resterait sur sa propre page d'erreur, sans code pour réessayer.
 */
async function fetchPublishedIndex(): Promise<string | null> {
  try {
    const response = await fetch('/', { cache: 'no-store', credentials: 'same-origin' })
    if (!response.ok) return null
    return await response.text()
  } catch {
    return null
  }
}

/**
 * Recharge la page sans perdre le secret de l'écran. Il est normalement gardé
 * dans `localStorage` ; sur un navigateur qui le refuse, on le remet dans le
 * fragment le temps du rechargement — `readViewerIdentity` l'en retire aussitôt.
 */
function reloadClockPage(displayId: string, token: string) {
  let stored: string | null = null
  try { stored = localStorage.getItem(`trafficplay.clock.${displayId}`) } catch { /* indisponible */ }
  if (stored !== token) history.replaceState(null, '', `/clock/${encodeURIComponent(displayId)}#${encodeURIComponent(token)}`)
  window.location.reload()
}

/**
 * Rétablit seul un écran Clock laissé ouvert : rechargement après 30 s de
 * désynchronisation (délai doublé à chaque échec, plafonné à 10 min), et
 * rechargement dès que le serveur publie une nouvelle version du frontend —
 * vérifiée à chaque reconnexion et toutes les 5 min.
 */
export function useClockRecovery({ displayId, token, active, stale, wsConnected }: Options) {
  const staleRef = useRef(stale)
  const staleSinceRef = useRef<number | null>(null)
  const healthySinceRef = useRef<number | null>(null)
  const busyRef = useRef(false)
  const wasConnectedRef = useRef(false)
  const everConnectedRef = useRef(false)

  useEffect(() => { staleRef.current = stale }, [stale])

  const checkVersion = useCallback(async () => {
    if (!token || busyRef.current) return
    busyRef.current = true
    try {
      const html = await fetchPublishedIndex()
      if (!html) return
      const origin = window.location.origin
      const state = readState()
      const published = bundleSignatureFromHtml(html, origin)
      if (!versionReloadDue(bundleSignatureFromDocument(document, origin), published, state)) return
      writeState({ ...state, versionTarget: published })
      reloadClockPage(displayId, token)
    } finally {
      busyRef.current = false
    }
  }, [displayId, token])

  const recoverFromStale = useCallback(async () => {
    if (!token || busyRef.current) return
    busyRef.current = true
    try {
      if (!await fetchPublishedIndex()) return
      const state = readState()
      writeState({ ...state, attempts: state.attempts + 1 })
      reloadClockPage(displayId, token)
    } finally {
      busyRef.current = false
    }
  }, [displayId, token])

  // Bandeau rouge persistant → rechargement ; synchro saine durable → remise
  // à zéro du compteur de tentatives.
  useEffect(() => {
    if (!active || !token) return
    const timer = window.setInterval(() => {
      const now = Date.now()
      if (staleRef.current) {
        staleSinceRef.current ??= now
        healthySinceRef.current = null
      } else {
        staleSinceRef.current = null
        healthySinceRef.current ??= now
      }
      const state = readState()
      if (healthySinceRef.current != null && now - healthySinceRef.current >= CLOCK_HEALTHY_RESET_MS && state.attempts) {
        writeState({ ...state, attempts: 0 })
      }
      const staleFor = staleSinceRef.current == null ? null : now - staleSinceRef.current
      if (staleReloadDue(staleFor, state.attempts)) void recoverFromStale()
    }, TICK_MS)
    return () => window.clearInterval(timer)
  }, [active, recoverFromStale, token])

  // Une reconnexion suit presque toujours un redémarrage du serveur, donc une
  // éventuelle mise à jour : c'est le moment de comparer les versions.
  useEffect(() => {
    if (!active) return
    if (wsConnected && !wasConnectedRef.current) {
      if (everConnectedRef.current) void checkVersion()
      everConnectedRef.current = true
    }
    wasConnectedRef.current = wsConnected
  }, [active, checkVersion, wsConnected])

  useEffect(() => {
    if (!active || !token) return
    const timer = window.setInterval(() => { void checkVersion() }, CLOCK_VERSION_CHECK_MS)
    return () => window.clearInterval(timer)
  }, [active, checkVersion, token])
}
